class VideoProcessor {
    constructor() {
        this.videoElement = document.createElement('video');
        this.videoElement.crossOrigin = 'anonymous'; // Important for canvas extraction
        this.videoElement.muted = true;
        this.canvas = document.createElement('canvas');
        this.ctx = this.canvas.getContext('2d');
    }

    /**
     * Load a video URL and return metadata
     * @param {string} url 
     * @returns {Promise<{duration: number, width: number, height: number}>}
     */
    async getVideoMetadata(url) {
        return new Promise((resolve, reject) => {
            if (!url) {
                reject(new Error('No URL provided'));
                return;
            }

            const video = document.createElement('video');
            video.preload = 'metadata';
            video.muted = true;
            video.crossOrigin = 'anonymous';

            video.onloadedmetadata = () => {
                resolve({
                    duration: video.duration,
                    width: video.videoWidth,
                    height: video.videoHeight
                });
                // Clean up source to stop loading if possible
                video.removeAttribute('src');
                video.load();
            };

            video.onerror = (e) => {
                reject(new Error(`Failed to load video metadata: ${e.type}`));
            };

            video.src = url;
        });
    }

    /**
     * Extract a frame at a specific timestamp
     * @param {string} url 
     * @param {number} time - Time in seconds
     * @param {object} options - { quality, type, width }
     * @returns {Promise<string>} Data URL of the frame
     */
    async extractFrame(url, time, options = {}) {
        const {
            quality = 0.8,
            type = 'image/jpeg',
            width = null // Optional explicit width
        } = options;

        return new Promise((resolve, reject) => {
            this.videoElement.src = url;
            this.videoElement.currentTime = time;

            // Wait for seek to complete and data to be available
            const onSeeked = () => {
                try {
                    // Set canvas size
                    const videoWidth = this.videoElement.videoWidth;
                    const videoHeight = this.videoElement.videoHeight;

                    // Allow scaling down if needed for performance
                    const targetWidth = width || videoWidth;
                    const scale = targetWidth / videoWidth;
                    const targetHeight = videoHeight * scale;

                    this.canvas.width = targetWidth;
                    this.canvas.height = targetHeight;

                    // Draw frame
                    this.ctx.drawImage(this.videoElement, 0, 0, targetWidth, targetHeight);

                    // cleanup listeners
                    this.videoElement.removeEventListener('seeked', onSeeked);
                    this.videoElement.removeEventListener('error', onError);

                    // Get data URL
                    const dataUrl = this.canvas.toDataURL(type, quality);
                    resolve(dataUrl);

                } catch (err) {
                    reject(err);
                }
            };

            const onError = (e) => {
                this.videoElement.removeEventListener('seeked', onSeeked);
                this.videoElement.removeEventListener('error', onError);
                reject(new Error('Video seek failed'));
            };

            this.videoElement.addEventListener('seeked', onSeeked);
            this.videoElement.addEventListener('error', onError);

            // Trigger load if needed (sometimes setting src isn't enough for seek)
            if (this.videoElement.readyState === 0) {
                this.videoElement.load();
            }
        });
    }

    /**
     * Batch extract frames for thumbnails
     * @param {string} url 
     * @param {number} count - Number of thumbnails to generate
     * @returns {Promise<string[]>} Array of Data URLs
     */
    async extractthumbnails(url, count = 5) {
        try {
            const meta = await this.getVideoMetadata(url);
            const interval = meta.duration / count;
            const thumbnails = [];

            for (let i = 0; i < count; i++) {
                const time = Math.min(i * interval + 0.1, meta.duration - 0.1); // Avoid exactly 0 or end sometimes
                const frame = await this.extractFrame(url, time, { width: 160, quality: 0.6 });
                thumbnails.push(frame);
            }
            return thumbnails;
        } catch (e) {
            console.error('Thumbnail generation failed:', e);
            return [];
        }
    }
}

// Singleton instance
export const videoProcessor = new VideoProcessor();
export default videoProcessor;
